import { Button, Dropdown, Icon } from "@app-components";
import styles from "@/styles/AddAdminModal.module.scss";
import { User } from "@/types";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { getCompanyFunction } from "@/api";
import { Tooltip } from "react-tooltip";

interface PersonSearchProps {
  header?: string;
  body?: string;
  noResultsText: string;
  dark?: boolean;
  showAddButton?: boolean;
  isSearching: boolean;
  searchQuery: string;
  searchForUser: (text: string) => Promise<User[] | undefined>;
  setSearchQuery: (value: string) => void;
  addUser: (user: User) => void;
  isAddButtonDisabled?: boolean;
  isLoading?: boolean;
  userAdded?: boolean;
  deleteHost?: () => void;
  showCompany?: boolean;
  infoText?: string;
  label?: string;
  required?: boolean;
  extraPaddingBottom?: boolean;
  disallowedUsers?: string[];
  disallowedMessage?: string;
  onSelectedUserChange?: (user: User | undefined) => void;
}

export const PersonSearch = ({
  header,
  body,
  dark,
  searchForUser,
  setSearchQuery,
  userAdded,
  deleteHost,
  showCompany,
  searchQuery,
  label,
  addUser,
  isLoading,
  isSearching,
  isAddButtonDisabled,
  showAddButton = false,
  extraPaddingBottom = false,
  disallowedUsers = [],
  disallowedMessage,
  onSelectedUserChange,
  infoText,
}: PersonSearchProps) => {
  const { t } = useTranslation();
  const [companyName, setCompanyName] = useState("");
  const [selectedUserOption, setSelectedUserOption] = useState<
    { label: string; value: string } | undefined
  >(undefined);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [foundUsers, setFoundUsers] = useState<User[]>([]);

  const isDisallowed = disallowedUsers?.some(
    (userId) => userId === selectedUser?.userId,
  );

  useEffect(() => {
    const fetchCompany = async () => {
      if (showCompany && selectedUser && selectedUser.buildingTenantId) {
        const company = await getCompanyFunction(
          selectedUser?.buildingTenantId,
        );
        if (company) {
          setCompanyName(company.buildingTenantName);
        }
      }
    };
    fetchCompany();
  }, [selectedUser, showCompany]);

  return (
    <div
      className={`${styles.addAdminCard} ${extraPaddingBottom ? styles.extraPaddingBottom : ""}`}
      onChange={(e) => e.stopPropagation()}
    >
      {userAdded ? (
        label ? (
          <p className={styles.userSelectedHeader}>{label}</p>
        ) : null
      ) : (
        <>
          {header && <h3>{header}</h3>}
          {body && (
            <p style={{ marginBottom: "var(--wlcm-spacing-sm)" }}>{body}</p>
          )}
        </>
      )}
      {!selectedUser && (
        <Dropdown
          required
          label={label}
          placeholder="ola@example.com"
          icon="search"
          searchingText={t("DEFAULT_SELECT.SEARCHING")}
          noResultsText={t("DEFAULT_SELECT.NO_RESULTS")}
          startSearchText={t("DEFAULT_SELECT.START_SEARCH")}
          dark={dark}
          fetchOptions={async (text: string) => {
            const users = await searchForUser(text);
            setFoundUsers(users || []);
            if (!users) return [];
            return users.map((user) => ({
              label: user.email,
              value: user.userId,
            }));
          }}
          defaultInputValue={searchQuery}
          value={selectedUserOption}
          isLoadingOptions={isSearching}
          onChange={(opt) => {
            setSelectedUserOption(opt);
            const user = foundUsers.find((u) => u.userId === opt?.value);
            setSelectedUser(user);
            onSelectedUserChange?.(user);
            if (user && !showAddButton) {
              addUser(user);
            }
          }}
          onInputChange={setSearchQuery}
          infoText={infoText}
        />
      )}
      <div className={`${styles.resultContainer} ${dark ? styles.dark : ""}`}>
        {selectedUser && (
          <>
            <div className={styles.userCard}>
              <Icon name="person" />
              <p>
                <strong>
                  {selectedUser.firstName} {selectedUser.lastName}
                </strong>{" "}
                ({selectedUser.email}){" "}
                <span style={{ opacity: 0.5 }}>
                  {showCompany && `${t("FROM")} ${companyName}`}
                </span>
              </p>
              {showAddButton && (
                <Button
                  disabled={isAddButtonDisabled || isDisallowed}
                  isLoading={isLoading}
                  onClick={() => addUser(selectedUser)}
                >
                  {t("ADD_USER.ADD_USER")}
                </Button>
              )}
              <button
                className={styles.deleteUserButton}
                onClick={() => {
                  setSelectedUser(undefined);
                  onSelectedUserChange?.(undefined);
                  if (deleteHost) {
                    setSelectedUserOption(undefined);
                    deleteHost();
                  }
                }}
              >
                <Icon name="delete" className="delete-user" />
                <Tooltip
                  anchorSelect=".delete-user"
                  content={t("PREREGISTRATION.DELETE_HOST")}
                />
                <span className="sr-only">
                  {t("PREREGISTRATION.DELETE_HOST")}
                </span>
              </button>
            </div>
            {isDisallowed && (
              <p className={styles.disallowedMessage}>
                {disallowedMessage ||
                  t(
                    "BUILDING_SETTINGS.ROLE_ASSIGNMENT_NOT_ALLOWED_FALLBACK_MESSAGE",
                  )}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
